import React from 'react';
import { Link } from 'react-router-dom';
import { BADGES } from '../utils/badges';
import { ArrowRight } from 'lucide-react';

interface BadgeCollectionProps {
  earnedBadges: string[];
  totalPlaytime: number;
  username: string;
}

export function BadgeCollection({ earnedBadges, totalPlaytime, username }: BadgeCollectionProps) {
  // Show only the first 4 badges in the preview
  const previewBadges = BADGES.slice(0, 4);
  const earnedCount = earnedBadges.length;

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Badges & Achievements</h2>
          <span className="text-sm text-gray-500">
            {earnedCount} of {BADGES.length} earned
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          {previewBadges.map((badge) => {
            const isEarned = earnedBadges.includes(badge.id);
            const Icon = badge.icon;
            const progress = Math.min(100, (totalPlaytime / badge.requirement) * 100);
            
            return (
              <div
                key={badge.id}
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  isEarned ? 'bg-indigo-50 border border-indigo-100' : 'bg-gray-50 border border-gray-100'
                }`}
              >
                <div className={`p-2 rounded-full ${
                  isEarned ? 'bg-indigo-100' : 'bg-gray-200'
                }`}>
                  <Icon className={`w-5 h-5 ${
                    isEarned ? 'text-indigo-600' : 'text-gray-400'
                  }`} />
                </div>
                <div className="min-w-0">
                  <h3 className={`font-medium text-sm truncate ${
                    isEarned ? 'text-indigo-900' : 'text-gray-500'
                  }`}>
                    {badge.name}
                  </h3>
                  <div className="w-full h-1 bg-gray-200 rounded-full mt-1">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isEarned ? 'bg-indigo-600' : 'bg-indigo-300'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <Link
          to={`/user/${username}/badges`}
          className="flex items-center justify-center gap-2 w-full py-2 text-indigo-600 hover:text-indigo-700 font-medium transition-colors group"
        >
          View All Badges
          <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
}